import React from 'react';
import styled from 'styled-components';
import { Calendar, MapPin, Phone, FileText, Users, Stethoscope } from 'lucide-react';
import { Appointment } from '../../services/appointmentsService';
import { CommitteeDto } from '../../services/committeesService';

interface UpcomingEventsListProps {
  appointments: Appointment[];
  committees: CommitteeDto[];
  onViewAll?: () => void;
}

const Container = styled.div`
  padding: 20px;
  /* No box styling - unified canvas */

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TitleIcon = styled.span`
  color: var(--text-secondary);
  display: flex;
  align-items: center;
`;

const ViewAllButton = styled.button`
  background: none;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 6px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
  }
`;

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0 -20px;

  @media (max-width: 767px) {
    margin: 0 -16px;
  }
`;

const EventCard = styled.div<{ $type: 'appointment' | 'committee' }>`
  padding: 14px 20px;
  border-right: 3px solid ${props => props.$type === 'committee'
    ? 'var(--primary-color)'
    : '#16a34a'};
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 767px) {
    padding: 12px 16px;
  }
`;

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 12px;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 6px;
  }
`;

const EventTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.3;
  flex: 1;
`;

const EventDate = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 500;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EventDetails = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const TypeBadge = styled.span<{ $type: 'appointment' | 'committee' }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$type === 'committee'
    ? 'rgba(0, 142, 162, 0.08)'
    : 'rgba(22, 163, 74, 0.08)'};
  color: ${props => props.$type === 'committee'
    ? 'var(--primary-color)'
    : '#16a34a'};
  border: 1px solid ${props => props.$type === 'committee'
    ? 'rgba(0, 142, 162, 0.25)'
    : 'rgba(22, 163, 74, 0.25)'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 20px;
  color: var(--text-secondary);
  font-size: 14px;
`;

const EmptyIconWrapper = styled.div`
  color: var(--text-secondary);
  margin-bottom: 12px;
  opacity: 0.5;
`;

const DetailIcon = styled.span`
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const UpcomingEventsList: React.FC<UpcomingEventsListProps> = ({
  appointments,
  committees,
  onViewAll
}) => {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `היום ${date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
    }

    if (diffDays === 1) {
      return `מחר ${date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
    }

    if (diffDays < 7) {
      return date.toLocaleDateString('he-IL', {
        weekday: 'long',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Combine and sort events
  const combinedEvents = [
    ...appointments.map(apt => ({
      type: 'appointment' as const,
      date: apt.appointmentDateTime || '',
      id: `apt-${apt.id}`,
      data: apt
    })),
    ...committees.map(comm => ({
      type: 'committee' as const,
      date: comm.committeeDate,
      id: `comm-${comm.id}`,
      data: comm
    }))
  ]
    .filter(event => event.date) // Only events with dates
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 3); // Show max 3 events

  return (
    <Container>
      <Header>
        <Title>
          <TitleIcon><Calendar size={16} strokeWidth={1.5} /></TitleIcon>
          אירועים קרובים
        </Title>
        {onViewAll && combinedEvents.length > 0 && (
          <ViewAllButton onClick={onViewAll}>
            כל האירועים
          </ViewAllButton>
        )}
      </Header>

      {combinedEvents.length === 0 ? (
        <EmptyState>
          <EmptyIconWrapper><Calendar size={40} strokeWidth={1.5} /></EmptyIconWrapper>
          <div>אין אירועים קרובים מתוזמנים</div>
        </EmptyState>
      ) : (
        <EventsList>
          {combinedEvents.map(event => {
            if (event.type === 'appointment') {
              const apt = event.data as Appointment;
              return (
                <EventCard key={event.id} $type="appointment">
                  <EventHeader>
                    <EventTitle>
                      <DetailIcon><Stethoscope size={14} strokeWidth={1.5} /></DetailIcon>
                      פגישה עם {apt.doctorName}
                      <TypeBadge $type="appointment">{apt.appointmentTypeName}</TypeBadge>
                    </EventTitle>
                    <EventDate>
                      <DetailIcon><Calendar size={12} strokeWidth={1.5} /></DetailIcon>
                      {formatDate(event.date)}
                    </EventDate>
                  </EventHeader>
                  <EventDetails>
                    {apt.address && (
                      <EventDetail>
                        <DetailIcon><MapPin size={12} strokeWidth={1.5} /></DetailIcon>
                        <span>{apt.address}</span>
                      </EventDetail>
                    )}
                    {apt.phoneForScheduling && (
                      <EventDetail>
                        <DetailIcon><Phone size={12} strokeWidth={1.5} /></DetailIcon>
                        <span>{apt.phoneForScheduling}</span>
                      </EventDetail>
                    )}
                    {apt.instructionsForClient && (
                      <EventDetail>
                        <DetailIcon><FileText size={12} strokeWidth={1.5} /></DetailIcon>
                        <span>{apt.instructionsForClient}</span>
                      </EventDetail>
                    )}
                  </EventDetails>
                </EventCard>
              );
            } else {
              const comm = event.data as CommitteeDto;
              return (
                <EventCard key={event.id} $type="committee">
                  <EventHeader>
                    <EventTitle>
                      <DetailIcon><Users size={14} strokeWidth={1.5} /></DetailIcon>
                      ועדה רפואית
                      <TypeBadge $type="committee">ועדה</TypeBadge>
                    </EventTitle>
                    <EventDate>
                      <DetailIcon><Calendar size={12} strokeWidth={1.5} /></DetailIcon>
                      {formatDate(event.date)}
                    </EventDate>
                  </EventHeader>
                  <EventDetails>
                    {comm.location && (
                      <EventDetail>
                        <DetailIcon><MapPin size={12} strokeWidth={1.5} /></DetailIcon>
                        <span>{comm.location}</span>
                      </EventDetail>
                    )}
                    {comm.escortNeeded && (
                      <EventDetail>
                        <DetailIcon><Users size={12} strokeWidth={1.5} /></DetailIcon>
                        <span>דרוש ליווי{comm.escortType && `: ${comm.escortType}`}</span>
                      </EventDetail>
                    )}
                    {comm.notes && (
                      <EventDetail>
                        <DetailIcon><FileText size={12} strokeWidth={1.5} /></DetailIcon>
                        <span>{comm.notes}</span>
                      </EventDetail>
                    )}
                  </EventDetails>
                </EventCard>
              );
            }
          })}
        </EventsList>
      )}
    </Container>
  );
};

export default UpcomingEventsList;
